import {AfterViewInit, Component, OnDestroy, ChangeDetectionStrategy} from '@angular/core';
import {ServiceMonitoringContext} from "./service-monitoring-context";
import {
  DateTimeRange,
  GetMessagingMetrics,
  GetMetrics,
  MessageType,
  MessagingMetrics,
  MetricsResult,
  RefreshMessagingMetrics,
  RefreshTrackerStats,
  RefreshTrackingResultMetrics,
  TrackerStats,
  TrackingMetric
} from "@portbase/bezoekschip-service-typescriptmodels";
import moment, {unitOfTime} from "moment";
import lodash, {Dictionary} from "lodash";
import {stringToRgba} from "./chart-utils";
import {sendQuery} from "../../../common/utils";
import {DateRange} from "../../../common/date/date-range/date-range";
import {ChartSerie} from "./service-monitoring-chart/service-monitoring-chart.component";
import {SerieConfig} from "./shared-chart-legend/shared-chart-legend.component";
import {BarSeriesOption, PieSeriesOption} from "echarts";

type TimeDefinitions = 'relative' | 'absolute';

// echarts 6 no longer publicly exports the internal PieDataItemOption type
// (previously imported from "echarts/types/src/chart/pie/PieSeries"). This
// mirrors the object item shape of a pie series' `data` array.
type PieDataItemOption = {
  id?: string | number;
  name?: string | number;
  groupId?: string | number;
  childGroupId?: string | number;
  value?: (number | '-')[] | number | '-';
  selected?: boolean;
};

@Component({
    selector: 'app-admin-dashboard',
    templateUrl: './service-monitoring.component.html',
    styleUrl: './service-monitoring.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ServiceMonitoringComponent implements AfterViewInit, OnDestroy {
  context = ServiceMonitoringContext;

  zoomed: boolean;
  dateTimeRange: DateTimeRange;
  timeFilter: TimeDefinitions = 'relative';
  timeValue: number = 1;
  measurement: unitOfTime.Base = "days";
  measurements: any[] = Object.values(MeasurementTypes).filter((v) => isNaN(Number(v)));
  private dataDebounceTime = 500;

  private hiddenLabels: string[];

  autoRefreshTimes: number[] = [5, 10, 15, 20, 30, 45, 60];
  currentAutoRefreshInSeconds: number;
  currentRefreshTimeout: any;

  // Datasets
  updatesPerUpdateType: { [key: string]: ChartSerie[] } = {};
  trackingApiResultData: ChartSerie;
  totalMessagingMetrics: { [P in MessageType]?: number } = {};
  totalAmountOfUpdatesPerConsumer: ChartSerie;
  serieConfig: SerieConfig[];
  totalUpdatesSerie: ChartSerie[];

  private cachedTrackingUserData: DashboardMetricsResult<TrackerStats> = {data: []};
  private cachedTrackingPollTimeData: DashboardMetricsResult<TrackingMetric> = {data: []};
  private cachedMessagingMetricsResult: DashboardMetricsResult<MessagingMetrics> = {data: []};
  private serieConfigMap: Dictionary<SerieConfig>;

  get getCurrentTimeRange(): string {
    return this.timeFilter === 'relative'
      ? `${this.timeValue} ${this.measurement} ago`
      : `${moment(this.dateTimeRange.start).format("DD-MMM-YYYY HH:mm:ss")} → ${moment(this.dateTimeRange.end).format("DD-MMM-YYYY HH:mm:ss")}`;
  }

  createTrackingTimeDataset(result: DashboardMetricsResult<TrackingMetric>, dateTimeRange: DateTimeRange) {
    this.cachedTrackingPollTimeData = result;
    this.trackingApiResultData = result.data?.length ? {
      name: "Tracking data",
      serie: <BarSeriesOption>{
        type: "bar",
        itemStyle: {
          color: "rgb(130,202,221,0.75)"
        },
        data: ServiceMonitoringContext.groupByTimeFrames(result.data.map(t => ({
          x: Date.parse(t.eventTime),
          y: t.differenceMillis
        })), dateTimeRange, lodash.mean).map(s => [s.x, s.y])
      }
    } : null;
  }

  createTrackerDatasets(result: DashboardMetricsResult<TrackerStats>, dateTimeRange: DateTimeRange) {
    this.cachedTrackingUserData = result;
    const totalDataPerUpdateType: { [updateType: string]: TrackerStatsRecord[] } = {};
    const totalDataPerUser: { [user: string]: TrackerStatsRecord[] } = {};
    result.data.filter(r => r.countPerUpdateType).forEach(r => {
      Object.entries(r.countPerUpdateType).forEach((e) => {
        const updateType = e[0];
        totalDataPerUpdateType[updateType] = totalDataPerUpdateType[updateType] || [];
        totalDataPerUpdateType[updateType].push({
          user: r.user,
          timestamp: r.timestamp,
          value: e[1]
        });
      });
      totalDataPerUser[r.user] = totalDataPerUser[r.user] || [];
      totalDataPerUser[r.user].push({
        user: r.user,
        timestamp: r.timestamp,
        value: lodash.sum(Object.values(r.countPerUpdateType))
      });
    });
    this.createCategories(totalDataPerUser, dateTimeRange);
    this.createTotalUpdates(totalDataPerUser, dateTimeRange);
    this.createUpdatesPerUpdateType(totalDataPerUpdateType, dateTimeRange);
    this.createTotalAmountOfUpdatesPerConsumer();
  }

  private createTotalAmountOfUpdatesPerConsumer() {
    this.totalAmountOfUpdatesPerConsumer = this.totalUpdatesSerie?.length ? {
      name: "Total amount of updates per consumer",
      serie: <PieSeriesOption>{
        type: "pie",
        radius: [20, 140],
        center: ['50%', '55%'],
        roseType: 'radius',
        label: {
          show: false
        },
        emphasis: {
          label: {
            show: true
          }
        },
        data: this.totalUpdatesSerie.map(s => ({
          value: lodash.sum(s.serie.data.map(r => r[1])),
          name: s.name,
          itemStyle: {
            color: this.serieConfigMap[s.name].color
          }
        }))
      }
    } : null;
  }

  private createUpdatesPerUpdateType(totalDataPerUpdateType: {
    [p: string]: TrackerStatsRecord[]
  }, dateTimeRange: DateTimeRange) {
    Object.entries(totalDataPerUpdateType).forEach(e => {
      this.updatesPerUpdateType[e[0]] = lodash.chain(e[1])
        .groupBy(r => r.user)
        .map((value, user) => (<ChartSerie>{
            name: user,
            serie: {
              name: user,
              type: 'line',
              stack: user,
              data: ServiceMonitoringContext.groupByTimeFrames(value.map(t => ({
                x: Date.parse(t.timestamp),
                y: t.value
              })), dateTimeRange, lodash.sum).map(s => [s.x, s.y]),
              lineStyle: {
                color: this.serieConfigMap[user].color,
              },
              itemStyle: {
                color: this.serieConfigMap[user].color,
              }
            }
          }
        )).value()
    });
  }

  private createTotalUpdates(totalDataPerUser: {
    [p: string]: TrackerStatsRecord[]
  }, dateTimeRange: DateTimeRange) {
    this.totalUpdatesSerie = lodash.map(Object.entries(totalDataPerUser).map(entry => {
      return <ChartSerie>{
        name: entry[0],
        serie: {
          name: entry[0],
          type: 'line',
          stack: entry[0],
          data: ServiceMonitoringContext.groupByTimeFrames(entry[1].map(t => ({
            x: Date.parse(t.timestamp),
            y: t.value
          })), dateTimeRange, lodash.sum).map(s => [s.x, s.y]),
          lineStyle: {
            color: this.serieConfigMap[entry[0]].color,
          },
          itemStyle: {
            color: this.serieConfigMap[entry[0]].color,
          }
        }
      }
    }));
  }

  private createCategories(totalDataPerUser: { [p: string]: TrackerStatsRecord[] }, dateTimeRange: DateTimeRange) {
    this.serieConfig = Object.entries(totalDataPerUser).map(e => ({
      name: e[0],
      selected: true,
      color: stringToRgba(e[0])
    }));
    this.serieConfigMap = lodash.keyBy(this.serieConfig, 'name');
  }

  createMessagingMetricsDataset(result: MetricsResult) {
    this.cachedMessagingMetricsResult = result;
    this.totalMessagingMetrics = {};
    lodash.forEach(lodash.groupBy(result.data, rec => rec.messageType), (rec, messageType) => {
      this.totalMessagingMetrics[messageType] = lodash.sum(rec.map(e => e.amount));
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.loadFromLocalStorage();
      this.setDateRange();
      this.debouncedGetTrackingMetrics();
    }, 0);
  }

  ngOnDestroy(): void {
    this.stopRefresh();
  }

  resetZoom() {
    this.zoomed = false;
    this.setDateRange();
  }

  onRefreshButtonClick = () => {
    if (this.currentRefreshTimeout) {
      this.stopRefresh();
    } else {
      this.refreshTrackingMetrics();
    }
  }

  private getTrackingMetrics = () => {
    this.reset();
    sendQuery("com.portbase.bezoekschip.common.api.metrics.GetTrackingResultMetrics", this.getDataPayload(), {
      caching: true
    }).subscribe((result: DashboardMetricsResult<TrackingMetric>) => {
      this.createTrackingTimeDataset(result, this.dateTimeRange);
    });
    sendQuery("com.portbase.bezoekschip.common.api.metrics.GetTrackerStats", this.getDataPayload(), {
      caching: true
    }).subscribe((result: DashboardMetricsResult<TrackerStats>) => {
      this.createTrackerDatasets(result, this.dateTimeRange);
    });
    sendQuery("com.portbase.bezoekschip.common.api.metrics.GetMessagingMetrics", <GetMessagingMetrics>{
      dateRange: this.getDataPayloadDateRange()
    }, {
      caching: true
    }).subscribe((result: DashboardMetricsResult<MessagingMetrics>) => {
      this.createMessagingMetricsDataset(result);
    });
  }

  private refreshTrackingMetrics = (): void => {
    sendQuery("com.portbase.bezoekschip.common.api.metrics.RefreshTrackingResultMetrics", <RefreshTrackingResultMetrics>{
      lastMatch: this.cachedTrackingPollTimeData?.lastMatch
    }).subscribe((result: DashboardMetricsResult<TrackingMetric>) => {
      result = this.mergeWithCachedData(result, this.cachedTrackingPollTimeData, (a => moment(a.eventTime)));
      this.createTrackingTimeDataset(result, this.dateTimeRange);
    });

    sendQuery("com.portbase.bezoekschip.common.api.metrics.RefreshTrackerStats", <RefreshTrackerStats>{
      lastMatch: this.cachedTrackingUserData?.lastMatch
    }).subscribe((result: DashboardMetricsResult<TrackerStats>) => {
      result = this.mergeWithCachedData(result, this.cachedTrackingUserData, (a => moment(a.timestamp)));
      this.createTrackerDatasets(result, this.dateTimeRange);
    });

    sendQuery("com.portbase.bezoekschip.common.api.metrics.RefreshMessagingMetrics", <RefreshMessagingMetrics>{
      lastMatches: this.cachedMessagingMetricsResult?.lastMatch
    }).subscribe((result: DashboardMetricsResult<MessagingMetrics>) => {
      result = this.mergeWithCachedData(result, this.cachedMessagingMetricsResult, (a => moment(a.timestamp)));
      this.createMessagingMetricsDataset(result);
    });
  }

  private debouncedGetTrackingMetrics = lodash.debounce(this.getTrackingMetrics, this.dataDebounceTime);

  refreshOverTime = (seconds: number): void => {
    this.currentAutoRefreshInSeconds = seconds;
    this.refreshTrackingMetrics();
    this.autoRefreshUsing(seconds);
  }

  onSelectionChange = (): void => {
    this.reset();
    this.setLocalStorage();
    this.resetZoom();
    this.debouncedGetTrackingMetrics();
  };

  measurementSelected = (value: unitOfTime.Base) => {
    if (value) {
      this.measurement = value;
      this.timeFilter = 'relative';
      this.onSelectionChange();
    }
  }

  onTimeValueChange = (timeValue: number) => {
    if (timeValue) {
      this.timeValue = timeValue;
      this.timeFilter = 'relative';
      this.onSelectionChange();
    }
  }

  onDateSelection = (dateRange: DateRange): void => {
    if (dateRange) {
      this.dateTimeRange = dateRange;
      this.timeFilter = 'absolute';
      this.stopRefresh();
      this.onSelectionChange();
    }
  }

  private setDateRange() {
    this.dateTimeRange = this.timeFilter === "relative"
      ? {
        start: moment().subtract(this.timeValue, this.measurement).toISOString(),
        end: moment().toISOString()
      } : this.dateTimeRange;
  };

  private getDataPayload = (): GetMetrics => ({
    dateRange: this.getDataPayloadDateRange()
  });

  private getDataPayloadDateRange = (): DateTimeRange => ({
    start: moment(this.dateTimeRange.start).toISOString(),
    end: moment(this.dateTimeRange.end).toISOString()
  })

  private autoRefreshUsing = (seconds: number): void => {
    if (this.currentRefreshTimeout) {
      clearTimeout(this.currentRefreshTimeout);
      this.currentRefreshTimeout = null;
    }
    this.currentRefreshTimeout = setTimeout(() => {
      if (this.currentRefreshTimeout) {
        this.setDateRange();
        this.refreshTrackingMetrics();
        this.autoRefreshUsing(seconds);
      }
    }, seconds * 1000);
  }

  private stopRefresh() {
    if (this.currentRefreshTimeout) {
      clearTimeout(this.currentRefreshTimeout);
      this.currentRefreshTimeout = null;
    }
    this.currentAutoRefreshInSeconds = null;
  }

  private setLocalStorage() {
    localStorage.setItem('admin-dashboard-days-preference', JSON.stringify(<LocalStorageData>{
      timeValue: this.timeValue,
      measurement: this.measurement,
      timeFilter: this.timeFilter,
      range: this.dateTimeRange
    }));
  }

  private loadFromLocalStorage(): void {
    const item = localStorage.getItem('admin-dashboard-days-preference');
    if (item) {
      const preference: LocalStorageData = JSON.parse(item);
      this.timeValue = preference.timeValue;
      this.measurement = preference.measurement;
      this.timeFilter = preference.timeFilter;
      this.dateTimeRange = preference.range;
    }
  }

  getUpdateTotalCount = (chartData: ChartSerie[], checkHiddenLabels: boolean = true): number => chartData
    ? lodash.sum(lodash.flatMap(chartData
      .filter(l => !checkHiddenLabels || !this.hiddenLabels?.includes(l.name))
      .map(d => d.serie.data.map(r => r[1]))))
    : null;

  get totalAmountOfUpdatesPerConsumerCount(): number {
    if (this.totalAmountOfUpdatesPerConsumer) {
      return lodash.sum((this.totalAmountOfUpdatesPerConsumer.serie.data as PieDataItemOption[])
          .filter((r: PieDataItemOption) => this.serieConfigMap[r.name].selected)
          .map((r: PieDataItemOption) => r.value));
    }
    return null;
  }

  private mergeWithCachedData<T>(result: DashboardMetricsResult<T>, cachedData: DashboardMetricsResult<T>, dateMapper: (value: T) => moment.Moment): DashboardMetricsResult<T> {
    result.data = cachedData.data.concat(result.data).filter(a => dateMapper(a).isBetween(this.dateTimeRange.start, this.dateTimeRange.end));
    result.lastMatch = result.lastMatch || cachedData.lastMatch;
    return result;
  }

  private reset() {
    this.updatesPerUpdateType = this.totalMessagingMetrics = {};
    this.trackingApiResultData = this.totalAmountOfUpdatesPerConsumer = this.serieConfig = this.totalUpdatesSerie = null;
  }
}

interface LocalStorageData {
  timeValue: number;
  range: DateTimeRange;
  measurement: unitOfTime.Base;
  timeFilter: TimeDefinitions;
}

enum MeasurementTypes {
  minutes = 60,
  hours = 360,
  days = 86400
}

interface TrackerStatsRecord {
  user: string;
  timestamp: string;
  value: number;
}

interface DashboardMetricsResult<T> {
  data?: T[];
  lastMatch?: any;
}
